import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { createPayment } from '../../services/payment';

const formatDate = (value) => value ? new Date(value).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

const PaymentModal = ({ open, onClose, config, activeBonus }) => {
  const quickAmounts = config?.payment?.amounts || [50, 100, 200];
  const minimum = Number(config?.payment?.minimum ?? 10);
  const maximum = Number(config?.payment?.maximum ?? 2000);
  const commission = Number(config?.payment?.commission_percent ?? 0);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setError(''); setAmount(''); } }, [open]);
  const numericAmount = Number(amount || 0);
  const total = Number.isFinite(numericAmount) ? numericAmount * (1 + commission / 100) : 0;
  const thresholdBonuses = config?.payment?.bonuses || {};
  const bestThreshold = Object.keys(thresholdBonuses).map(Number).filter((value) => numericAmount >= value).sort((a, b) => b - a)[0];
  const thresholdPercent = Number(thresholdBonuses[bestThreshold] || 0);
  const thresholdBonus = numericAmount * thresholdPercent / 100;
  const rouletteBonusApplies = activeBonus && numericAmount >= Number(activeBonus.min_amount || 0);
  const rouletteBonus = rouletteBonusApplies ? Math.min(numericAmount * Number(activeBonus.percent || 0) / 100, Number(activeBonus.max_bonus || 0)) : 0;
  const credited = numericAmount + thresholdBonus + rouletteBonus;
  const apiErrorText = (err) => {
    const detail = err.response?.data?.detail;
    if (Array.isArray(detail)) return String(detail[0]?.msg || 'Не удалось создать платёж').replace(/^Value error, /, '');
    return detail || 'Не удалось создать платёж';
  };

  const pay = async () => {
    if (!Number.isFinite(numericAmount) || numericAmount < minimum || numericAmount > maximum) {
      setError(`Введите сумму от ${minimum} до ${maximum} ₽`); return;
    }
    setBusy(true); setError('');
    try {
      const result = await createPayment(numericAmount);
      window.location.assign(result.confirmation_url);
    } catch (err) {
      setError(apiErrorText(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Пополнить баланс</DialogTitle>
      <DialogContent>
        {busy && <Alert icon={<CircularProgress size={18} color="inherit"/>} severity="info" sx={{ mb: 2 }}><b>Создаём платёж…</b><br/>Подождите, сейчас откроется защищённая страница оплаты.</Alert>}
        <Typography color="text.secondary" mb={2}>Выберите сумму пополнения. После оплаты баланс обновится автоматически.</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {activeBonus && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Бонусный круг даст +{activeBonus.percent}% к этому пополнению при сумме от {Number(activeBonus.min_amount).toLocaleString('ru-RU')} ₽.
            Максимум бонуса — {Number(activeBonus.max_bonus).toLocaleString('ru-RU')} ₽, действует до {formatDate(activeBonus.expires_at)}.
          </Alert>
        )}
        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
          {quickAmounts.map((value) => <Button disabled={busy} key={value} variant={Number(amount) === Number(value) ? 'contained' : 'outlined'} onClick={() => setAmount(String(value))}>{value} ₽</Button>)}
        </Stack>
        <TextField disabled={busy} fullWidth label="Сумма к зачислению, ₽" type="number" value={amount} onChange={(e) => setAmount(e.target.value.replace(',', '.'))} inputProps={{ min: minimum, max: maximum, step: '0.01' }} />
        <Box mt={2} p={2} border="1px solid" borderColor="divider" borderRadius={3}>
          <Stack direction="row" justifyContent="space-between"><Typography color="text.secondary">Пополнение</Typography><Typography>{numericAmount > 0 ? numericAmount.toFixed(2) : '0.00'} ₽</Typography></Stack>
          {!!thresholdPercent && <Stack direction="row" justifyContent="space-between" mt={1}><Typography color="text.secondary">Бонус за сумму +{thresholdPercent}%</Typography><Typography color="primary.light">+{thresholdBonus.toFixed(2)} ₽</Typography></Stack>}
          {!!rouletteBonus && <Stack direction="row" justifyContent="space-between" mt={1}><Typography color="text.secondary">Бонус круга +{activeBonus.percent}%</Typography><Typography color="primary.light">+{rouletteBonus.toFixed(2)} ₽</Typography></Stack>}
          <Stack direction="row" justifyContent="space-between" mt={1.5}><Typography fontWeight={800}>На баланс</Typography><Typography fontWeight={900}>{credited > 0 ? credited.toFixed(2) : '0.00'} ₽</Typography></Stack>
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          К оплате: <b>{total.toFixed(2)} ₽</b>
          {commission > 0 ? <> · комиссия {commission}%</> : null}
        </Alert>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={busy}>Отмена</Button><Button startIcon={busy ? <CircularProgress size={17} color="inherit"/> : null} variant="contained" onClick={pay} disabled={busy || !amount}>{busy ? 'Создаём…' : 'Продолжить'}</Button></DialogActions>
    </Dialog>
  );
};

export default PaymentModal;
